/**
 * Auto-sizes the lessons booking iframe from the scheduler's own reported height, and
 * reveals a link-out card when the scheduler never reports one.
 *
 * Cal.com posts `__dimensionChanged` to the parent whenever its content height
 * changes, provided the embed URL carries `embed=`. That happens with no vendor
 * script loaded, so the theme reads the message directly rather than shipping a
 * third-party <script> (a RemoteAsset offense the ratchet floor has no room for).
 *
 * That message is also the only liveness signal available: the frame is cross-origin,
 * so nothing else about it is readable, and a frame blocked by the visitor's own
 * content blocker still fires no error the parent can see. No message within
 * STALL_MS of the frame entering the viewport therefore stands in for "blocked", and
 * a message arriving later reverses it.
 *
 * Only a Cal host gets this element around it (the section decides), because a
 * scheduler that reports no height by design would otherwise stall forever.
 *
 * The reported height is not taken on faith. Cal re-measures itself whenever we resize the
 * frame, and on a Cal EVENT page that re-measure comes back short by the page's own header
 * and footer, so applying it gives the frame a scrollbar inside the page's scrollbar. See
 * SETTLE_MS.
 *
 * Progressive enhancement: with this module absent the iframe still loads and books,
 * at the section's authored fallback height.
 */

const MIN_HEIGHT = 320;

/**
 * A sanity ceiling on a hostile or buggy value, not a layout budget. It has to clear the
 * TALLEST surface this band frames, because a clamped frame scrolls inside the page — the
 * nested scrollbar this module exists to avoid. The compact booker is under 1200px, but a
 * Cal EVENT page is a content page: the Oct 17 clinic measures 2348px at 320px wide, and
 * it grows with its own description. Measure before lowering this.
 */
const MAX_HEIGHT = 4000;

/**
 * How long a height we just applied is treated as authoritative against a SMALLER one.
 *
 * Cal re-measures itself whenever we resize the frame, so its next report is provoked by
 * our own write. On a correct embed that re-measure returns the same number. Cal's event
 * pages return a smaller one — they report `<main>` and omit their own header and footer
 * (a constant 117px, measured at every width) — and applying it leaves the frame 117px
 * short of its content, which is a scrollbar inside the page's scrollbar. So a shrink
 * arriving inside this window is Cal contradicting the measurement it just gave us, and
 * is dropped rather than deferred: re-applying it later is the same bug on a delay.
 *
 * Only a shrink is held off, and only right after our own write. Growth always applies at
 * once (that is the direction that causes the nested scrollbar), and a visitor advancing a
 * step is never inside this window, so a genuine step-to-step shrink still lands.
 *
 * The cost is whitespace: if Cal's first report is too TALL and its correction arrives
 * inside the window, the frame stays tall until the next report. Cheaper than a scrollbar.
 */
const SETTLE_MS = 400;

/**
 * The frame's real origin is not necessarily the one the section pointed it at: Cal serves
 * `www.cal.com` as a 308 to the apex, and a team can be moved to an org subdomain. So the
 * trust boundary is the cal.com host family over https, not one authored string. An exact
 * match would reject every message from a redirected frame, and the ten-second stall would
 * then replace a WORKING calendar with the recovery card.
 *
 * Deliberately no label charset: the section's own `is_cal_host` accepts any characters
 * before `.cal.com`, and this rule has to stay the LOOSER of the two. A host the section
 * wraps but this rejects is that same regression again, while the other direction costs
 * nothing — the wrapper only ever exists around a src the section already matched.
 */
const CAL_ORIGIN = /^https:\/\/([^/]+\.)?cal\.com$/;

/**
 * How long to wait for the first height before treating the frame as blocked. Cal is a
 * heavy app and the frame is lazy-loaded, so this is deliberately generous: a false
 * positive briefly shows a card that then hides itself, which is a far cheaper mistake
 * than a visitor who cannot book.
 */
const STALL_MS = 10000;

class NeuroCalEmbed extends HTMLElement {
  #onMessage = (event) => this.#handle(event);
  #stallTimer = null;
  #observer = null;
  // The last height this module wrote, and whether it is still inside its settle window.
  // Held as a flag cleared by a timer rather than a timestamp so the window is driven by
  // the same clock as the stall clock.
  #appliedHeight = null;
  #settleTimer = null;
  // Once the frame has proved it is alive, it stays proved. Re-arming the clock on a
  // later connect would raise the card over a calendar that is working, because Cal
  // posts on CHANGE and has nothing to re-send.
  #gotHeight = false;

  connectedCallback() {
    window.addEventListener('message', this.#onMessage);
    this.#watchForVisibility();
  }

  disconnectedCallback() {
    window.removeEventListener('message', this.#onMessage);
    this.#clearStallTimer();
    this.#clearSettleTimer();
    this.#observer?.disconnect();
    this.#observer = null;
  }

  get #iframe() {
    return this.querySelector('iframe');
  }

  get #recover() {
    return this.querySelector('[data-nlbk-recover]');
  }

  /**
   * The stall clock starts when the frame reaches the viewport, not at page load: the
   * iframe is lazy, so a frame still below the fold has not begun loading and has
   * nothing to report yet. Without IntersectionObserver the clock starts immediately,
   * which is the same order of events on a browser that also ignores `loading`.
   */
  #watchForVisibility() {
    if (this.#gotHeight) return;
    if (typeof IntersectionObserver !== 'function') {
      this.#startStallTimer();
      return;
    }

    this.#observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.#observer?.disconnect();
      this.#observer = null;
      this.#startStallTimer();
    });
    this.#observer.observe(this);
  }

  #startStallTimer() {
    // Guarded here rather than at the call sites: a viewport callback registered before
    // the first height can still fire afterwards, so this is the one chokepoint every
    // path to the stall state goes through.
    if (this.#gotHeight) return;
    this.#clearStallTimer();
    this.#stallTimer = setTimeout(() => {
      this.#stallTimer = null;
      // Re-checked at FIRE time as well as arm time: the arm-time guard is only sufficient
      // while the flag and the clear stay in one synchronous block.
      if (this.#gotHeight) return;
      this.#setStalled(true);
    }, STALL_MS);
  }

  #clearStallTimer() {
    if (this.#stallTimer === null) return;
    clearTimeout(this.#stallTimer);
    this.#stallTimer = null;
  }

  #clearSettleTimer() {
    if (this.#settleTimer === null) return;
    clearTimeout(this.#settleTimer);
    this.#settleTimer = null;
  }

  /**
   * Write a height and open the settle window against a shrink provoked by that write.
   * The window is re-armed on every write, so a run of growth keeps it open throughout.
   */
  #applyHeight(iframe, height) {
    this.#appliedHeight = height;
    iframe.style.height = `${height}px`;
    this.#clearSettleTimer();
    this.#settleTimer = setTimeout(() => {
      this.#settleTimer = null;
    }, SETTLE_MS);
  }

  #setStalled(stalled) {
    const recover = this.#recover;
    if (stalled) {
      // Gated on the card existing: the stalled state collapses the frame to zero height,
      // so without a card to put in its place it would leave no booking affordance at all.
      if (!recover) return;
      this.dataset.embedState = 'stalled';
      recover.hidden = false;
      return;
    }
    delete this.dataset.embedState;
    if (recover) recover.hidden = true;
  }

  /**
   * The scheduler is a third-party origin, so every field is checked before use:
   * the message has to come from this element's own frame, the payload has to be
   * the shape we expect, and the height is clamped — a hostile or buggy value
   * must not be able to blow out the page.
   */
  #handle(event) {
    const iframe = this.#iframe;
    if (!iframe || event.source !== iframe.contentWindow) return;

    // A frame that navigated itself off Cal keeps the same contentWindow and would
    // otherwise still pass the identity check above.
    if (!CAL_ORIGIN.test(event.origin)) return;

    let payload = event.data;
    if (typeof payload === 'string') {
      try {
        payload = JSON.parse(payload);
      } catch {
        return;
      }
    }
    if (!payload || payload.originator !== 'CAL') return;
    if (payload.type !== '__dimensionChanged') return;

    // Everything above proves the frame is ALIVE: its own window, a Cal origin, Cal's own
    // dimension message. That is what the stall clock is asking about, not the number the
    // message carries, so proof of life is latched here and nowhere earlier.
    //
    // The two must not be conflated. The stalled state collapses the frame to zero height,
    // and Cal measures its own document inside that zero-height viewport, so a stalled
    // frame can legitimately report 0. Requiring a usable height to LIFT the stall would
    // make that state self-sustaining and bury a working calendar for good.
    this.#gotHeight = true;
    this.#clearStallTimer();
    this.#setStalled(false);

    // `> 0`, not merely finite, for the WRITE: `Number(null)`, `Number('')` and
    // `Number([])` are all a finite 0, which would pin the frame at the clamp floor.
    const reported = Number(payload.data?.iframeHeight);
    if (!Number.isFinite(reported) || reported <= 0) return;

    const height = Math.min(Math.max(reported, MIN_HEIGHT), MAX_HEIGHT);

    // Cal contradicting, within the settle window, the height it just made us write. See
    // SETTLE_MS: on this vendor that report is short by its own chrome, and honouring it
    // is the nested scrollbar. Equal heights are not a shrink and re-arm the window.
    //
    // The open window IS the baseline: nothing arms the timer but a write, and a write
    // always sets the height first, so a separate null check on it would be dead.
    if (this.#settleTimer !== null && height < this.#appliedHeight) return;

    this.#applyHeight(iframe, height);
  }
}

if (!customElements.get('neuro-cal-embed')) {
  customElements.define('neuro-cal-embed', NeuroCalEmbed);
}
